import { FEATURE_FLAGS } from "./featureFlag.js";

export const INSIGHTS_TIME_FRAMES = ["event", "app", "custom"];

const timeFrameQuerystring = {
  type: "object",
  properties: {
    timeFrame: { type: "string", enum: INSIGHTS_TIME_FRAMES, default: "event" },
    dateFrom: { type: "string", format: "date" },
    dateTo: { type: "string", format: "date" },
  },
  additionalProperties: false,
};

const insightsParams = {
  type: "object",
  properties: {
    eventId: { type: "string" },
  },
  required: ["eventId"],
};

// Lifetime endpoints: data is always computed over the full app-availability
// window, so no time frame is accepted.
export const getInsightsSchema = {
  params: insightsParams,
};

// Endpoints that still honour a caller-selected range (PostHog aggregates and
// the date-range resolver): most-viewed entities and pages.
export const getRangedInsightsSchema = {
  params: insightsParams,
  querystring: timeFrameQuerystring,
};

export const INSIGHTS_ANALYSIS_GROUPS = {
  SUMMARY: "summary",
  ATTENDANCE: "attendance",
  COMMUNICATIONS: "communications",
  REACH: "reach",
  ON_SITE: "onSite",
  REGISTRATION: "registration",
  ENGAGEMENT: "engagement",
  COMPLIANCE: "compliance",
};

export const INSIGHTS_ANALYSIS_DEFAULT_TEXT =
  "Event Analysis will be available shortly after the event.";

export const INSIGHTS_ANALYSIS_SECTIONS = [
  {
    key: "registeredAttendees",
    group: INSIGHTS_ANALYSIS_GROUPS.SUMMARY,
    featureFlag: null,
    parts: [],
  },
  {
    key: "checkInRate",
    group: INSIGHTS_ANALYSIS_GROUPS.SUMMARY,
    featureFlag: null,
    parts: [],
  },
  {
    key: "engagementIndex",
    group: INSIGHTS_ANALYSIS_GROUPS.SUMMARY,
    featureFlag: null,
    parts: [],
  },
  {
    key: "appFeatureUsage",
    group: INSIGHTS_ANALYSIS_GROUPS.SUMMARY,
    featureFlag: null,
    parts: [],
  },
  {
    key: "sponsorReach",
    group: INSIGHTS_ANALYSIS_GROUPS.SUMMARY,
    featureFlag: null,
    parts: [],
  },
  {
    key: "attendeeAccess",
    group: INSIGHTS_ANALYSIS_GROUPS.ATTENDANCE,
    featureFlag: null,
    parts: [
      "totalTickets",
      "checkedIn",
      "checkInRate",
      "checkInsOverTime",
      "appPageViews",
      "viewsPerPage",
    ],
  },
  {
    key: "agendaUsage",
    group: INSIGHTS_ANALYSIS_GROUPS.ATTENDANCE,
    featureFlag: null,
    parts: [
      "bookmarks",
      "activeAppUsers",
      "avgSessionsFollowed",
      "sessionsTracked",
      "mostViewedSessions",
      "agendaViewsOverTime",
      "highestRatedSessions",
    ],
  },
  {
    key: "profileViews",
    group: INSIGHTS_ANALYSIS_GROUPS.ATTENDANCE,
    featureFlag: null,
    parts: ["topSpeakers", "topSponsors"],
  },
  {
    key: "pagesUsage",
    group: INSIGHTS_ANALYSIS_GROUPS.ATTENDANCE,
    featureFlag: null,
    parts: ["activeAppUsers", "openedPageRate", "pagesTracked", "viewsPerPage"],
  },
  {
    key: "notifications",
    group: INSIGHTS_ANALYSIS_GROUPS.COMMUNICATIONS,
    featureFlag: null,
    parts: [
      "emailSent",
      "emailOpenRate",
      "notificationsSent",
      "notificationOpenRate",
      "byType",
    ],
  },
  {
    key: "appAdoption",
    group: INSIGHTS_ANALYSIS_GROUPS.REACH,
    featureFlag: null,
    parts: [
      "activeAppUsers",
      "checkedInAttendees",
      "appActivationRate",
      "languageDistribution",
    ],
  },
  {
    key: "qrCheckIn",
    group: INSIGHTS_ANALYSIS_GROUPS.ON_SITE,
    featureFlag: FEATURE_FLAGS.CHECK_IN,
    parts: [
      "totalCheckedIn",
      "peakCheckInWindow",
      "checkedInFirstTwoHours",
      "lateArrivals",
      "checkInsPerHour",
      "profileBreakdown",
      "ticketTypeAttendance",
      "checkInsPerHourByDay",
    ],
  },
  {
    key: "inviteFunnel",
    group: INSIGHTS_ANALYSIS_GROUPS.REGISTRATION,
    featureFlag: FEATURE_FLAGS.INVITE_EMAILS,
    parts: [],
  },
  {
    key: "registrations",
    group: INSIGHTS_ANALYSIS_GROUPS.REGISTRATION,
    featureFlag: FEATURE_FLAGS.REGISTRATIONS,
    parts: [
      "approvalRate",
      "checkInRate",
      "avgHoursToCheckIn",
      "registrationFunnel",
      "attendance",
      "registrationsOverTime",
    ],
  },

  {
    key: "pollsAnalysis",
    group: INSIGHTS_ANALYSIS_GROUPS.ENGAGEMENT,
    featureFlag: FEATURE_FLAGS.POLLS,
    parts: [
      "totalPolls",
      "totalResponses",
      "participants",
      "participationRate",
      "avgResponsesPerPoll",
      "perPollBreakdown",
    ],
  },
  {
    key: "surveysAnalysis",
    group: INSIGHTS_ANALYSIS_GROUPS.ENGAGEMENT,
    featureFlag: FEATURE_FLAGS.SURVEYS,
    parts: [
      "totalSurveys",
      "totalResponses",
      "participants",
      "participationRate",
      "perSurveyBreakdown",
    ],
  },
  {
    key: "giveaway",
    group: INSIGHTS_ANALYSIS_GROUPS.ENGAGEMENT,
    featureFlag: FEATURE_FLAGS.GIVEAWAYS,
    parts: [
      "sponsors",
      "totalScans",
      "participants",
      "completionRate",
      "mostPopularSponsor",
      "scansPerSponsor",
    ],
  },
  {
    key: "interestsTags",
    group: INSIGHTS_ANALYSIS_GROUPS.ENGAGEMENT,
    featureFlag: FEATURE_FLAGS.ATTENDEE_INTERESTS_TAGS,
    parts: ["attendeeCategories", "mostCommonInterests"],
  },
  {
    key: "engagementSegmentation",
    group: INSIGHTS_ANALYSIS_GROUPS.ENGAGEMENT,
    featureFlag: null,
    parts: [],
  },
  {
    key: "compliance",
    group: INSIGHTS_ANALYSIS_GROUPS.COMPLIANCE,
    featureFlag: FEATURE_FLAGS.EVENT_COMPLIANCE,
    parts: [
      "accepted",
      "declined",
      "optedOut",
      "seen",
      "neverOpened",
      "deletedAccount",
      "complianceAnswers",
    ],
  },
];

export const INSIGHTS_ANALYSIS_SECTION_KEYS = INSIGHTS_ANALYSIS_SECTIONS.map(
  (s) => s.key
);

export const INSIGHTS_ANALYSIS_PART_KEYS = [
  ...new Set(INSIGHTS_ANALYSIS_SECTIONS.flatMap((s) => s.parts)),
];

export const patchInsightsAnalysisSchema = {
  params: {
    type: "object",
    properties: { eventId: { type: "string" } },
    required: ["eventId"],
  },
  body: {
    type: "object",
    properties: {
      sections: {
        type: "object",
        propertyNames: { enum: INSIGHTS_ANALYSIS_SECTION_KEYS },
        additionalProperties: {
          type: "object",
          properties: {
            text: { type: "string" },
            isEnabled: { type: "boolean" },
            parts: {
              type: "object",
              propertyNames: { enum: INSIGHTS_ANALYSIS_PART_KEYS },
              additionalProperties: { type: "boolean" },
            },
          },
          additionalProperties: false,
        },
      },
      isEnabled: { type: "boolean" },
    },
    anyOf: [{ required: ["sections"] }, { required: ["isEnabled"] }],
  },
};
